import { Button, styled } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Text, { TextSpan } from '../../Atoms/Text';

import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { FileUploadResponse, uploadMergeFile } from '../../../store/Link';
import { ProgressBar } from '../../Atoms/ProgressBar';
import { addError } from '../../../store/Error';
import { UploadSummary } from './Summary';

export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export const BulkMergePage = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<FileUploadResponse>();
  const [file, setFile] = useState<File | undefined>();
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [progress, setProgress] = useState(1);
  const [startProgressIncrement, setStartProgressIncrement] = useState(false);
  const [incrementValue, setIncrementValue] = useState(0);
  const navigate = useNavigate();

  const progressHandler = (progressEvent: { loaded: number; total: number }) => {
    const { loaded, total } = progressEvent;
    const percentage = Math.round(((loaded / total) * 100) / 3);
    setProgress(percentage);
    setStartProgressIncrement(true);
  };

  const handleFileChange = async (files: File[]) => {
    const file = files[0];
    if (file) {
      setFileSize(file.size);
      setFileName(file.name);
      setFile(file);
    }
  };

  const handleContinue = async () => {
    setUploadResponse(undefined);
    setUploading(true);
    if (file) {
      setFileSize(file.size);
      setFileName(file.name);
      const { data, error } = await uploadMergeFile(file, progressHandler);
      if (error) addError('link', error.message);
      if (data) {
        setShowSummary(true);
        setUploadResponse(data);
      }
    }
    setUploading(false);
  };

  const handleReset = () => {
    setShowSummary(false);
    setUploadResponse(undefined);
    setFile(undefined);
    setFileName('');
    setFileSize(0);
    setProgress(1);
  };

  useEffect(() => {
    if (startProgressIncrement && incrementValue === 0) {
      const sizeInMb = fileSize / 10000;
      const increment = 66 / sizeInMb;
      setIncrementValue(Math.round(increment));
    }

    if (startProgressIncrement && incrementValue > 0 && progress < 100) {
      setTimeout(() => setProgress(progress + incrementValue > 100 ? 100 : progress + incrementValue), 500);
    }

    if (startProgressIncrement && incrementValue > 0 && progress >= 100) {
      setStartProgressIncrement(false);
      setIncrementValue(0);
    }
  }, [startProgressIncrement, progress, incrementValue]);

  return (
    <PageLayout
      className="w-full"
      pageTitle="Merge Links"
      dataTestId="bulk-merge-page">
      <PageCard
        title="Upload Merge File"
        className="mt-12"
        childrenWrapperClass="px-8 py-8 space-y-3">
        <>
          {!uploading && !showSummary && (
            <div
              className="space-y-2"
              data-testid="upload-file-form-container">
              <div className="flex space-x-4 items-center">
                <Button
                  className="flex items-center"
                  component="label"
                  size="small"
                  color="primary"
                  variant="contained"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}>
                  Select file
                  <VisuallyHiddenInput
                    ref={fileInputRef}
                    onChange={(e) => handleFileChange(Array.from(e.target.files as FileList))}
                    type="file"
                    accept=".xlsx"
                  />
                </Button>
                <Text
                  weight="medium"
                  className="pl-1"
                  size="sm14">
                  {fileName}
                </Text>
              </div>
              <Text size="sm14">
                Upload must be “Account merge history.xlsx” from the RPM “Account merge history” page.
              </Text>
            </div>
          )}
          {uploading && !showSummary && (
            <div className="px-4">
              <div className="m-auto w-3/4 flex justify-between items-center align-top">
                <div className="w-[97%]">
                  <ProgressBar
                    progress={progress}
                    fullWidth={true}
                    progressBarStyleOverride="h-full bg-indigo transition rounded"
                    className="flex align-middle rounded"
                  />
                </div>
                <TextSpan
                  size="sm14"
                  color="grey6">
                  {progress}%
                </TextSpan>
              </div>
            </div>
          )}
          {!uploading && showSummary && uploadResponse && <UploadSummary uploadResponse={uploadResponse} />}
          <div className="pt-6 border-b"></div>
          <div className="w-full">
            <div className="flex space-x-2 flex-row justify-end">
              {showSummary && (
                <>
                  <button
                    onClick={handleReset}
                    className={DEFAULT_CANCEL_BUTTON_STYLE}>
                    Back
                  </button>
                  <button
                    onClick={() => navigate('/data/rpm-cleanup')}
                    className={DEFAULT_ADVANCE_BUTTON_STYLE}>
                    Done
                  </button>
                </>
              )}
              {!showSummary && (
                <button
                  disabled={!file}
                  className={DEFAULT_ADVANCE_BUTTON_STYLE}
                  onClick={handleContinue}>
                  Continue
                </button>
              )}
            </div>
          </div>
        </>
      </PageCard>
    </PageLayout>
  );
};
